/**
 * Which language tree the page being rendered belongs to.
 *
 * Provided by the harness — `mountClient` and `createRenderBody` both wrap the
 * app in it, from the SAME payload field (`locale`) they take the router
 * basename from, so server and browser can never disagree about either. A
 * component never reads `window.__CMS_DATA__.locale` itself.
 *
 * `undefined` on a single-language site, which is every site that has not
 * published a translation; every helper here is then the identity.
 */
import { createContext, useCallback, useContext } from 'react'
import { localizeHref, routerPath, type PublishedLocale } from '@gjl/types'
import { uiString, type UiStringKey, UI_STRINGS_EN } from './ui-strings'

/** Provided by `<LocaleProvider>` (./LocaleProvider.tsx) — the harness's, not a site's. */
export const LocaleContext = createContext<PublishedLocale | undefined>(undefined)

/** The current tree's locale, or `undefined` on a single-language site. */
export function useLocale(): PublishedLocale | undefined {
  return useContext(LocaleContext)
}

/**
 * `href → href in this tree`, for links CODE builds (`/faculty/${id}`, the
 * logo's `/`). Links from content were rewritten by the publish and pass
 * through unchanged.
 */
export function useLocalizeHref(): (href: string) => string {
  const locale = useLocale()
  return useCallback((href: string) => localizeHref(href, locale), [locale])
}

/**
 * `href → <Link to>` for this tree: strips the basename a published href may
 * already carry, because react-router adds it again. Use for every `<Link to>`
 * fed from content.
 */
export function useRouterPath(): (href: string) => string {
  const locale = useLocale()
  return useCallback((href: string) => routerPath(href, locale), [locale])
}

/** `t('nav.open')` — the platform's own words in the current locale. */
export function useUiStrings(): <K extends UiStringKey>(key: K) => (typeof UI_STRINGS_EN)[K] {
  const code = useLocale()?.code
  return useCallback(<K extends UiStringKey>(key: K) => uiString(code, key), [code])
}
